import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { trackCreate, useOpenedGoal } from "@/lib/metrica";
import { MOBILE_STICKY_FOOTER } from "@/lib/utils";
import { BillingChoice } from "@/components/BillingChoice";
import {
  InvoiceItemsEditor, serializeInvoiceItems, makeEmptyInvoiceItem,
} from "@/components/InvoiceItemsEditor";

/**
 * Shared "New / Edit Purchase" dialog — the ONLY place that knows how
 * to POST a purchase (and chain POST /invoices when Faturalı is
 * selected).  Consumed by:
 *
 *   * Purchases.jsx   — free vendor picker (vendors prop)
 *   * VendorDetail.jsx — vendor prop locked (dropdown hidden,
 *                        vendor_id auto-injected)
 *
 * Contract: pass EITHER ``vendors`` (dropdown mode) OR ``lockedVendor``
 * (implicit id mode).  Never both.  Edit flow (``editing`` non-null)
 * never emits the inline invoice — invoice CRUD lives on Faturalar.
 */
const EMPTY = {
  vendor_id: "", customer_id: "", job_id: "",
  category: "Diğer", date: todayISO(),
  description: "", amount: 0,
  invoice_status: "invoiced", cash_account: "company",
  due_date: "", notes: "",
  invoice_date: todayISO(),
  invoice_number: "",
  invoice_items: null,
};

export default function PurchaseFormDialog({
  open, onOpenChange, editing,
  vendors, lockedVendor,
  customers = [], jobs = [], categories = [],
  onSaved,
}) {
  const [form, setForm] = useState(EMPTY);
  useOpenedGoal("purchase_create_opened", open && !editing);
  const [vatRates, setVatRates] = useState([]);
  // Edit + faturalı akışta yüklenen mevcut alış faturaları.
  //   * 1 fatura      → aynı invoice PUT ile güncellenir (duplicate yok).
  //   * N fatura      → info bloğu, Faturalar sayfasına yönlendirme.
  //   * 0 fatura      → info bloğu (fatura yok mesajı).
  const [existingInvoices, setExistingInvoices] = useState([]);
  const [editingInvoiceId, setEditingInvoiceId] = useState(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/vat-rates?status=active")
      .then((r) => setVatRates(r.data || []))
      .catch(() => setVatRates([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...EMPTY, ...editing,
        vendor_id: lockedVendor?.id || editing.vendor_id || "",
        job_id: editing.job_id || "",
        due_date: editing.due_date || "",
      });
    } else {
      setForm({
        ...EMPTY,
        vendor_id: lockedVendor?.id || "",
        // Yeni satın almada faturalandırma ve kasa kullanıcı tarafından seçilir (zorunlu).
        invoice_status: "", cash_account: "",
      });
    }
    setExistingInvoices([]);
    setEditingInvoiceId(null);
  }, [open, editing, lockedVendor]);

  // Edit + Faturalı akışta satın alma üzerindeki mevcut alış faturalarını çek.
  useEffect(() => {
    if (!open || !editing) return;
    if ((editing.invoice_status || "invoiced") !== "invoiced") return;
    let cancelled = false;
    setLoadingInvoices(true);
    api.get(`/invoices?type=purchase&purchase_id=${encodeURIComponent(editing.id)}`)
      .then((r) => {
        if (cancelled) return;
        const list = r.data || [];
        setExistingInvoices(list);
        if (list.length === 1) {
          const inv = list[0];
          setEditingInvoiceId(inv.id);
          setForm((f) => ({
            ...f,
            invoice_date: inv.invoice_date || todayISO(),
            invoice_number: inv.invoice_number || "",
            invoice_items: (inv.items || []).map((it) => ({
              description: it.description || "",
              quantity: String(it.quantity ?? 1),
              unit_price: String(it.unit_price ?? 0),
              vat_rate: String(it.vat_rate ?? 20),
            })),
          }));
        }
      })
      .catch(() => { if (!cancelled) setExistingInvoices([]); })
      .finally(() => { if (!cancelled) setLoadingInvoices(false); });
    return () => { cancelled = true; };
  }, [open, editing]);

  const activeCategories = useMemo(
    () => (categories ?? []).filter((c) => (c.status ?? "active") === "active"),
    [categories],
  );

  const jobsForCustomer = useMemo(() => {
    if (!form.customer_id) return [];
    return (jobs ?? []).filter((j) => j.customer_id === form.customer_id);
  }, [jobs, form.customer_id]);

  const inlineInvoiceSubtotal = useMemo(() => {
    if (!Array.isArray(form.invoice_items)) return 0;
    return form.invoice_items.reduce(
      (s, it) => s + (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0),
      0,
    );
  }, [form.invoice_items]);

  // Faturalı inline invoice görünümü ve Tutar disabled kararı:
  //   * CREATE + faturalı              → yeni fatura oluşturulur (POST)
  //   * EDIT + faturalı + 1 fatura     → mevcut fatura PUT ile güncellenir
  //   * EDIT + faturalı + N/0 fatura   → aşağıda ayrı UI (kalem editörü kapalı)
  const isInvoicedCreate = !editing && form.invoice_status === "invoiced";
  // Mobil (yeni kayıt): Fatura Bilgileri Faturalandırma'nın hemen altında; sonrası bu gruba.
  const mAfter = editing ? "" : "max-md:order-1";
  const isInvoicedSingleEdit =
    !!editing && form.invoice_status === "invoiced" && existingInvoices.length === 1;
  const isInvoicedFlow = isInvoicedCreate || isInvoicedSingleEdit;
  const showMultiInvoiceInfo =
    !!editing && form.invoice_status === "invoiced" && existingInvoices.length > 1;
  const showNoInvoiceInfo =
    !!editing && form.invoice_status === "invoiced" &&
    !loadingInvoices && existingInvoices.length === 0;

  const onSubmit = async (e) => {
    e.preventDefault();
    const vendorId = lockedVendor?.id || form.vendor_id;
    if (!vendorId) { toast.error("Tedarikçi seçin"); return; }
    if (!editing && !form.invoice_status) { toast.error("Faturalandırma seçin: Faturalı veya Faturasız"); return; }
    if (!editing && !form.cash_account) { toast.error("Lütfen kasa seçimi yapın."); return; }

    const wantsInlineInvoice =
      isInvoicedFlow &&
      form.invoice_status === "invoiced" &&
      Array.isArray(form.invoice_items) &&
      form.invoice_items.length > 0 &&
      form.invoice_items.some((it) => parseFloat(it.unit_price) > 0);

    if (wantsInlineInvoice) {
      if (!form.invoice_date) { toast.error("Fatura tarihini girin"); return; }
      for (let i = 0; i < form.invoice_items.length; i++) {
        const it = form.invoice_items[i];
        if (!(it.description || "").trim()) {
          toast.error(`${i + 1}. kalem: Açıklama girin`); return;
        }
        if ((parseFloat(it.quantity) || 0) <= 0) {
          toast.error(`${i + 1}. kalem: Miktar 0'dan büyük olmalı`); return;
        }
        if ((parseFloat(it.unit_price) || 0) <= 0) {
          toast.error(`${i + 1}. kalem: Birim fiyat 0'dan büyük olmalı`); return;
        }
      }
    }

    try {
      const {
        invoice_date: _invDate, invoice_number: _invNo,
        invoice_items: _invItems, ...purchaseBody
      } = form;
      const payload = {
        ...purchaseBody,
        vendor_id: vendorId,
        amount: (wantsInlineInvoice || isInvoicedCreate)
          ? parseFloat(inlineInvoiceSubtotal.toFixed(2))
          : (parseFloat(form.amount) || 0),
        customer_id: form.customer_id || null,
        job_id: form.job_id || null,
        due_date: form.due_date || null,
      };
      let purchaseId;
      if (editing) {
        await api.put(`/purchases/${editing.id}`, payload);
        purchaseId = editing.id;
      } else {
        const { data } = await trackCreate("purchase", () => api.post("/purchases", payload));
        purchaseId = data.id;
      }

      if (wantsInlineInvoice && purchaseId) {
        try {
          const invoicePayload = {
            type: "purchase",
            vendor_id: vendorId,
            purchase_id: purchaseId,
            invoice_date: form.invoice_date,
            invoice_number: (form.invoice_number || "").trim(),
            items: serializeInvoiceItems(form.invoice_items),
          };
          if (isInvoicedSingleEdit && editingInvoiceId) {
            // Mevcut alış faturası PUT ile güncellenir — YENİ duplicate
            // invoice oluşturulmaz.  invoice.id korunur; tedarikçi borcu
            // ve KDV raporu backend derived hesapları üzerinden otomatik
            // güncel değer üretir.
            await api.put(`/invoices/${editingInvoiceId}`, invoicePayload);
            toast.success("Satın alma ve alış faturası güncellendi");
          } else {
            await api.post("/invoices", invoicePayload);
            toast.success("Satın alma ve alış faturası oluşturuldu");
          }
        } catch (err) {
          toast.error(
            (isInvoicedSingleEdit
              ? "Satın alma güncellendi ancak fatura güncellenemedi: "
              : "Satın alma kaydedildi ancak fatura oluşturulamadı: ") +
            (err?.response?.data?.detail || "Fatura sonradan düzenlenmelidir"),
          );
        }
      } else {
        toast.success(editing ? "Satın alma güncellendi" : "Satın alma eklendi");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "İşlem başarısız");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`max-w-4xl max-h-[92vh] overflow-y-auto ${editing ? "" : "max-md:max-h-[calc(100dvh_-_1rem)] max-md:overflow-x-hidden"}`}
                     data-testid="purchase-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {editing ? "Satın Alma Düzenle" : "Yeni Satın Alma"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className={`grid grid-cols-1 md:grid-cols-2 gap-4 ${editing ? "" : "[&>*]:min-w-0"}`}
              data-testid="purchase-form">
          <Field label="Tarih *" type="date" value={form.date}
                 onChange={(v) => setForm({ ...form, date: v })}
                 required testId="purchase-date-input" />
          <Field label="Vade Tarihi" type="date" value={form.due_date}
                 onChange={(v) => setForm({ ...form, due_date: v })} />

          {/* Vendor picker OR locked readout */}
          {lockedVendor ? (
            <div className="md:col-span-2 flex items-center gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                 data-testid="purchase-vendor-locked">
              <span className="uppercase tracking-wider">Tedarikçi</span>
              <span className="font-medium text-slate-800">
                {lockedVendor.name}
                {lockedVendor.company_name ? ` — ${lockedVendor.company_name}` : ""}
              </span>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="text-xs">Tedarikçi *</Label>
              <Select value={form.vendor_id}
                      onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="purchase-vendor-select">
                  <SelectValue placeholder="Seçin" />
                </SelectTrigger>
                <SelectContent>
                  {(vendors ?? []).map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs">Bu gider hangi müşteri için yapıldı?</Label>
            <Select value={form.customer_id || "none"}
                    onValueChange={(v) => setForm({
                      ...form, customer_id: v === "none" ? "" : v, job_id: "",
                    })}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="purchase-customer-select">
                <SelectValue placeholder="Müşteri seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Genel Şirket Gideri</SelectItem>
                {(customers ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">İş (opsiyonel)</Label>
            <Select value={form.job_id || "none"}
                    onValueChange={(v) => setForm({ ...form, job_id: v === "none" ? "" : v })}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="purchase-job-select">
                <SelectValue placeholder="(opsiyonel)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— Yok —</SelectItem>
                {(jobsForCustomer ?? []).map((j) => (
                  <SelectItem key={j.id} value={j.id}>{j.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {form.customer_id && !form.job_id && (
              <p
                className="text-[11px] text-amber-700 bg-amber-50/60 border border-amber-200
                           rounded-md px-2.5 py-1.5 leading-snug"
                data-testid="purchase-no-job-hint"
              >
                İş seçmezseniz bu satın alma müşteri kârlılığına dahil olur
                ancak iş bazlı kârlılıkta görünmez.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Kategori</Label>
            <Select value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="purchase-category-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(activeCategories ?? []).map((c) => (
                  <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                ))}
                {editing && form.category
                  && !(activeCategories ?? []).some((c) => c.name === form.category) && (
                  <SelectItem value={form.category}>{form.category} (Pasif)</SelectItem>
                )}
              </SelectContent>
            </Select>
          </div>

          {!editing && (
            <BillingChoice testIdPrefix="purchase-billing" value={form.invoice_status}
                           onChange={(v) => setForm({ ...form, invoice_status: v })} />
          )}
          <Field
            className={mAfter}
            label={isInvoicedFlow
              ? "Tutar (KDV Hariç, kalemlerden otomatik)"
              : "Tutar (₺) *"}
            type="number" step="0.01"
            value={isInvoicedFlow
              ? inlineInvoiceSubtotal.toFixed(2)
              : form.amount}
            onChange={(v) => setForm({ ...form, amount: v })}
            disabled={isInvoicedFlow}
            required={!isInvoicedFlow}
            testId="purchase-amount-input"
          />
          {editing && (<div className="space-y-1.5">
            <Label className="text-xs">Fatura Durumu</Label>
            <Select value={form.invoice_status}
                    onValueChange={(v) => setForm({ ...form, invoice_status: v })}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="purchase-invoice-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="invoiced">Faturalı</SelectItem>
                <SelectItem value="non_invoiced">Faturasız</SelectItem>
              </SelectContent>
            </Select>
          </div>)}
          <div className={`space-y-1.5 ${mAfter}`}>
            <Label className="text-xs">{editing ? "Varsayılan Kasa" : "Kasa Seçimi *"}</Label>
            <Select value={form.cash_account}
                    onValueChange={(v) => setForm({ ...form, cash_account: v })}>
              <SelectTrigger className="h-10 rounded-lg" data-testid="purchase-cash-account-select">
                <SelectValue placeholder="Seçin" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="company">Şirket Kasası</SelectItem>
                <SelectItem value="personal">Şahsi Kasa</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className={`md:col-span-2 space-y-1.5 ${mAfter}`}>
            <Label className="text-xs">Açıklama</Label>
            <Input value={form.description}
                   onChange={(e) => setForm({ ...form, description: e.target.value })}
                   className="h-10 rounded-lg" />
          </div>
          <div className={`md:col-span-2 space-y-1.5 ${mAfter}`}>
            <Label className="text-xs">Notlar</Label>
            <Textarea value={form.notes}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      rows={2} />
          </div>

          {/* Inline fatura — CREATE + faturalı VEYA EDIT + faturalı + tek fatura. */}
          {isInvoicedFlow && (
            <div className="md:col-span-2 space-y-3 border border-slate-200 rounded-xl p-4 bg-slate-50/50"
                 data-testid="purchase-invoice-section">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-800">
                  {isInvoicedSingleEdit ? "Mevcut Fatura" : "Fatura Bilgileri"}
                </div>
                {isInvoicedSingleEdit && (
                  <span className="text-[11px] text-slate-500" data-testid="purchase-invoice-editing-badge">
                    Bu fatura güncellenecek — yeni fatura oluşturulmaz.
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Field label="Fatura Tarihi *" type="date"
                       value={form.invoice_date}
                       onChange={(v) => setForm({ ...form, invoice_date: v })}
                       testId="purchase-invoice-date-input" />
                <Field label="Fatura No" value={form.invoice_number || ""}
                       onChange={(v) => setForm({ ...form, invoice_number: v })}
                       testId="purchase-invoice-number-input" />
                <div className="text-[11px] text-slate-500 self-end pb-2">
                  Tedarikçi: satın alma kaydından otomatik alınır.
                </div>
              </div>
              <InvoiceItemsEditor
                items={form.invoice_items && form.invoice_items.length
                  ? form.invoice_items
                  : [makeEmptyInvoiceItem(vatRates)]}
                setItems={(next) => setForm({ ...form, invoice_items: next })}
                vatRates={vatRates}
                testIdPrefix="purchase-invoice-item"
                mobileCards={!editing}
              />
              <p className="text-[11px] text-slate-500 leading-relaxed">
                {isInvoicedSingleEdit
                  ? "Kalem, tarih veya fatura numarasında yaptığınız değişiklikler mevcut alış faturası üzerine kaydedilir. Satın alma tutarı otomatik olarak KDV hariç toplama eşitlenir; tedarikçi borcu ve KDV raporu backend hesapları üzerinden güncellenir."
                  : "Fatura kalemlerinin KDV-hariç toplamı otomatik olarak satın alma tutarına yazılır. Tedarikçi borcu KDV-dahil genel toplam olarak hesaplanır."}
              </p>
            </div>
          )}

          {showMultiInvoiceInfo && (
            <div className="md:col-span-2 space-y-2 border border-amber-200 rounded-xl p-4 bg-amber-50/60"
                 data-testid="purchase-multi-invoice-info">
              <div className="text-sm font-medium text-amber-900">
                Bu satın almaya bağlı {existingInvoices.length} fatura bulunuyor
              </div>
              <p className="text-[11px] text-amber-800 leading-relaxed">
                Bir satın almaya birden fazla alış faturası kesildiğinde bu form
                kalemleri birleştirmez. Her faturayı ayrı ayrı düzenlemek için
                Faturalar sayfasını kullanın.
              </p>
              <ul className="text-xs text-amber-900 space-y-1 pl-4 list-disc">
                {existingInvoices.map((inv) => (
                  <li key={inv.id} data-testid={`purchase-multi-invoice-row-${inv.id}`}>
                    <span className="font-medium">
                      {inv.invoice_number || "(No yok)"}
                    </span>
                    {" · "}
                    <span>{inv.invoice_date}</span>
                    {" · "}
                    <span>₺{Number(inv.grand_total || 0).toLocaleString("tr-TR")}</span>
                  </li>
                ))}
              </ul>
              <a href="/invoices"
                 className="inline-flex items-center gap-1 text-xs font-medium text-amber-900 hover:underline mt-1"
                 data-testid="purchase-goto-invoices-link">
                Faturalar sayfasına git →
              </a>
            </div>
          )}

          {showNoInvoiceInfo && (
            <div className="md:col-span-2 space-y-2 border border-slate-200 rounded-xl p-4 bg-slate-50/50"
                 data-testid="purchase-no-invoice-info">
              <div className="text-sm font-medium text-slate-800">
                Bu satın alma faturalı görünüyor ancak henüz kesilmiş fatura yok
              </div>
              <p className="text-[11px] text-slate-500 leading-relaxed">
                Fatura eklemek için Faturalar sayfasını kullanabilirsiniz.
                Fatura Durumunu "Faturasız" olarak değiştirirseniz Tutar manuel girilebilir.
              </p>
              <a href="/invoices"
                 className="inline-flex items-center gap-1 text-xs font-medium text-slate-800 hover:underline mt-1"
                 data-testid="purchase-goto-invoices-link-empty">
                Faturalar sayfasına git →
              </a>
            </div>
          )}

          <DialogFooter
            className={editing ? "md:col-span-2"
              : `md:col-span-2 max-md:order-last ${MOBILE_STICKY_FOOTER} max-md:pb-[max(0.75rem,env(safe-area-inset-bottom))]`}
            data-testid="purchase-form-footer">
            <Button type="button" variant="outline"
                    onClick={() => onOpenChange(false)}>Vazgeç</Button>
            <Button type="submit" className="bg-slate-900 hover:bg-slate-800"
                    data-testid="purchase-save-button">Kaydet</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, required, type = "text", step, testId, disabled, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs">{label}</Label>
      <Input value={value ?? ""} type={type} step={step}
             onChange={(e) => onChange(e.target.value)}
             required={required} disabled={disabled}
             className="h-10 rounded-lg" data-testid={testId} />
    </div>
  );
}
